import { useState } from "react";
import { fmt, ResultCard } from "./pipe-helpers";
import { Input } from "@/components/ui/input";

/* ═══════════════════════════════════════════════════════
   PIPE THREAD & ENGAGEMENT REFERENCE TOOL
   NPT thread data, engagement depths, tap drill sizes,
   and sealant recommendations
   ═══════════════════════════════════════════════════════ */

interface ThreadEntry {
  nominal: string;
  tpi: number;
  od: number;         // major OD inches
  pitch: number;      // inches
  minEngage: number;  // hand-tight engagement L1
  effectiveEngage: number; // effective thread L2
  tapDrill: string;
  tapDrillDec: number;
  threadDepth: number;
}

const NPT_DATA: ThreadEntry[] = [
  { nominal: '1/8"', tpi: 27, od: 0.405, pitch: 0.03704, minEngage: 0.1615, effectiveEngage: 0.2639, tapDrill: "R (11/32)", tapDrillDec: 0.339, threadDepth: 0.02963 },
  { nominal: '1/4"', tpi: 18, od: 0.540, pitch: 0.05556, minEngage: 0.2278, effectiveEngage: 0.4018, tapDrill: "7/16", tapDrillDec: 0.4375, threadDepth: 0.04444 },
  { nominal: '3/8"', tpi: 18, od: 0.675, pitch: 0.05556, minEngage: 0.2400, effectiveEngage: 0.4078, tapDrill: "37/64", tapDrillDec: 0.5781, threadDepth: 0.04444 },
  { nominal: '1/2"', tpi: 14, od: 0.840, pitch: 0.07143, minEngage: 0.3200, effectiveEngage: 0.5337, tapDrill: "23/32", tapDrillDec: 0.7188, threadDepth: 0.05714 },
  { nominal: '3/4"', tpi: 14, od: 1.050, pitch: 0.07143, minEngage: 0.3390, effectiveEngage: 0.5457, tapDrill: "59/64", tapDrillDec: 0.9219, threadDepth: 0.05714 },
  { nominal: '1"', tpi: 11.5, od: 1.315, pitch: 0.08696, minEngage: 0.4000, effectiveEngage: 0.6828, tapDrill: "1-5/32", tapDrillDec: 1.1563, threadDepth: 0.06957 },
  { nominal: '1-1/4"', tpi: 11.5, od: 1.660, pitch: 0.08696, minEngage: 0.4200, effectiveEngage: 0.7068, tapDrill: "1-1/2", tapDrillDec: 1.5000, threadDepth: 0.06957 },
  { nominal: '1-1/2"', tpi: 11.5, od: 1.900, pitch: 0.08696, minEngage: 0.4200, effectiveEngage: 0.7235, tapDrill: "1-23/32", tapDrillDec: 1.7188, threadDepth: 0.06957 },
  { nominal: '2"', tpi: 11.5, od: 2.375, pitch: 0.08696, minEngage: 0.4360, effectiveEngage: 0.7565, tapDrill: "2-3/16", tapDrillDec: 2.1875, threadDepth: 0.06957 },
  { nominal: '2-1/2"', tpi: 8, od: 2.875, pitch: 0.12500, minEngage: 0.6820, effectiveEngage: 1.1375, tapDrill: "2-5/8", tapDrillDec: 2.6250, threadDepth: 0.10000 },
  { nominal: '3"', tpi: 8, od: 3.500, pitch: 0.12500, minEngage: 0.7660, effectiveEngage: 1.2000, tapDrill: "3-1/4", tapDrillDec: 3.2500, threadDepth: 0.10000 },
  { nominal: '4"', tpi: 8, od: 4.500, pitch: 0.12500, minEngage: 0.8440, effectiveEngage: 1.3000, tapDrill: "4-1/4", tapDrillDec: 4.2500, threadDepth: 0.10000 },
  { nominal: '6"', tpi: 8, od: 6.625, pitch: 0.12500, minEngage: 0.9580, effectiveEngage: 1.5125, tapDrill: "6-3/8", tapDrillDec: 6.3750, threadDepth: 0.10000 },
];

const SEALANT_INFO = [
  { method: "Teflon Tape", use: "General purpose, water, air, gas. 3-5 wraps clockwise.", temp: "Up to 500°F", pressure: "Up to 10,000 PSI" },
  { method: "Yellow Tape (Gas)", use: "Natural gas, propane, fuel lines. Thicker & denser.", temp: "Up to 500°F", pressure: "Up to 10,000 PSI" },
  { method: "Pipe Dope", use: "Threaded joints, hydraulic, steam. Apply to male threads.", temp: "Varies by compound", pressure: "Up to 10,000 PSI" },
  { method: "Anaerobic Sealant", use: "Metal-to-metal threads, vibration resistance.", temp: "Up to 400°F", pressure: "Up to 10,000 PSI" },
];

export const PipeThreadReference = () => {
  const [search, setSearch] = useState("");
  const [selectedSize, setSelectedSize] = useState<string | null>(null);

  const filtered = search
    ? NPT_DATA.filter((t) => t.nominal.toLowerCase().includes(search.toLowerCase()))
    : NPT_DATA;

  const selected = selectedSize ? NPT_DATA.find((t) => t.nominal === selectedSize) : null;

  return (
    <div className="space-y-4">
      <div className="bg-card border border-border rounded-xl p-3">
        <p className="text-xs text-muted-foreground leading-relaxed">
          <span className="text-foreground font-semibold">NPT thread reference</span> — engagement depths, tap drill sizes, thread geometry, and sealant guide. Tap a size for detail.
        </p>
      </div>

      <Input placeholder="Search pipe size…" value={search} onChange={(e) => setSearch(e.target.value)} className="font-mono-data bg-card border-border" />

      {selected && (
        <div className="space-y-2 animate-fade-in">
          <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            {selected.nominal} NPT Detail
          </h3>
          <ResultCard label="Threads Per Inch" value={String(selected.tpi)} unit="TPI" accent />
          <ResultCard label="Major OD" value={fmt(selected.od, 3)} unit="in" />
          <ResultCard label="Pitch" value={fmt(selected.pitch, 5)} unit="in" />
          <ResultCard label="Thread Depth" value={fmt(selected.threadDepth, 5)} unit="in" />
          <ResultCard label="Min Engagement (L1)" value={fmt(selected.minEngage, 4)} unit="in" accent />
          <ResultCard label="Effective Engagement (L2)" value={fmt(selected.effectiveEngage, 4)} unit="in" />
          <ResultCard label="Tap Drill" value={selected.tapDrill} unit="" accent />
          <ResultCard label="Tap Drill (decimal)" value={fmt(selected.tapDrillDec, 4)} unit="in" />
          <div className="mt-2">
            <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1">Thread Turns</h4>
            <div className="grid grid-cols-2 gap-2">
              <ResultCard label="Hand-tight turns" value={fmt(selected.minEngage / selected.pitch, 1)} unit="turns" />
              <ResultCard label="Wrench-tight turns" value={fmt(selected.effectiveEngage / selected.pitch, 1)} unit="turns" />
            </div>
          </div>
          <button onClick={() => setSelectedSize(null)} className="text-xs text-primary font-semibold mt-2">← Back to table</button>
        </div>
      )}

      {!selected && (
        <div className="overflow-x-auto -mx-4 animate-fade-in">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-border">
                <th className="text-left px-4 py-2 text-muted-foreground font-semibold">Size</th>
                <th className="text-right px-3 py-2 text-muted-foreground font-semibold">TPI</th>
                <th className="text-right px-3 py-2 text-muted-foreground font-semibold">Engage</th>
                <th className="text-right px-3 py-2 text-muted-foreground font-semibold">Tap Drill</th>
                <th className="text-right px-4 py-2 text-muted-foreground font-semibold">OD</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((t) => (
                <tr key={t.nominal} onClick={() => setSelectedSize(t.nominal)}
                  className="border-b border-border/50 cursor-pointer hover:bg-secondary/50 active:bg-secondary transition-colors">
                  <td className="px-4 py-2.5 font-mono-data font-semibold text-primary">{t.nominal}</td>
                  <td className="px-3 py-2.5 font-mono-data text-right text-foreground">{t.tpi}</td>
                  <td className="px-3 py-2.5 font-mono-data text-right text-foreground">{fmt(t.effectiveEngage, 3)}"</td>
                  <td className="px-3 py-2.5 font-mono-data text-right text-foreground">{t.tapDrill}</td>
                  <td className="px-4 py-2.5 font-mono-data text-right text-foreground">{t.od}"</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Sealant Guide */}
      <div className="mt-4">
        <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">Thread Sealant Guide</h3>
        <div className="space-y-2">
          {SEALANT_INFO.map((s) => (
            <div key={s.method} className="bg-card border border-border rounded-xl p-3">
              <div className="text-sm font-semibold text-foreground">{s.method}</div>
              <p className="text-[11px] text-muted-foreground mt-0.5">{s.use}</p>
              <div className="flex gap-4 mt-1.5 text-[10px] text-muted-foreground">
                <span>🌡️ {s.temp}</span>
                <span>💪 {s.pressure}</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
